import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Typography } from 'antd';

import DataState, { deriveDataState } from '../loading/DataState';
import Skeleton from '../loading/Skeleton';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { countItems } from '../../services/items';
import { cardPagePath } from '../../utils/cards';
import { canManage } from '../../utils/permissions';

const { Text } = Typography;

/**
 * After a card change, the card the items left (KPE-028, KPR-710).
 *
 * When the change moved a whole card, the card it left may be empty. This
 * OFFERS what to do with it — retire it or delete it, both in the card's
 * Danger zone — and forces nothing: the card stays as it is until the person
 * decides. It says nothing while items remain on the card, and nothing to
 * anyone who cannot manage the collection (the Danger zone is theirs).
 *
 * Drawn by the background task's row (a whole card always runs as a task);
 * the review step's own move (KPR-709) can draw it too.
 */
export default function ChangeCardSourceOffer( { collectionId, card } ) {
	const context = useCollectionContext();
	const collection = context?.collection || null;
	const cardId = card?.id || null;
	const mayManage = Boolean( collection && canManage( collection ) );
	const [ left, setLeft ] = useState( null );
	const [ loading, setLoading ] = useState( true );
	// A courtesy: a count that could not be read offers nothing and says nothing.
	const [ failed, setFailed ] = useState( false );

	const load = useCallback( async () => {
		if ( ! cardId || ! mayManage ) return;
		setLoading( true );
		setFailed( false );
		try {
			setLeft( await countItems( { collection_id: collectionId, card_id: cardId } ) );
		} catch ( e ) {
			setFailed( true );
		} finally {
			setLoading( false );
		}
	}, [ collectionId, cardId, mayManage ] );

	useEffect( () => { load(); }, [ load ] );

	if ( ! cardId || ! mayManage || failed ) return null;
	// Only an empty card is offered; items still on it say nothing.
	const hasData = left !== null;
	const isEmpty = hasData && left > 0;
	return (
		<DataState
			state={deriveDataState( { loading, hasData, isEmpty } )}
			hasData={hasData}
			isEmpty={isEmpty}
			skeleton={<Skeleton variant='line' count={1} />}
			empty={<span />}
			error={null}
			onRetry={load}
			label='whether the card is empty'
			className='imports-task__offer'
		>
			<Text className='imports-list__line' data-testid='change-card-source-offer'>
				{`No items are left on “${ card.name || 'the card' }”. `}
				<Link to={cardPagePath( cardId, collectionId, 'tab=danger' )}>Retire or delete the card</Link>
				{' if you no longer need it.'}
			</Text>
		</DataState>
	);
}
